// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the answer to a starting point (Konvo panel, and the same card in the Assistant
// DM): the count as the headline, what was checked, a one-line summary, then the items
// most urgent first. Each item has an urgency ring (red = due or blocking, amber = open,
// grey = can wait), its sources as small chips that open the preview window, and up to
// two icon actions with tooltips that go back to the assistant as a follow-up.

import styles from '@app/features/konvo_assistant/components/AssistantFindings.module.css';
import {openPreview, SourceMark} from '@app/features/konvo_assistant/components/AssistantSourceCards';
import {
	ArrowBendUpLeftIcon,
	ArrowSquareOutIcon,
	CalendarBlankIcon,
	CheckIcon,
	CheckSquareIcon,
	NoteIcon,
	TimerIcon,
	UserPlusIcon,
} from '@app/features/ui/icons/Icons';
import {Tooltip} from '@app/features/ui/tooltip/Tooltip';
import type {
	AssistantFindingsBlock,
	AssistantFindingsItem,
	AssistantSourceItem,
} from '@konvo/schema/src/domains/konvo/AssistantResponse';
import {clsx} from 'clsx';

const RING: Record<AssistantFindingsItem['urgency'], string | undefined> = {
	high: styles.ringHigh,
	medium: styles.ringMedium,
	low: undefined,
};
const DUE: Record<AssistantFindingsItem['urgency'], string | undefined> = {
	high: styles.dueHigh,
	medium: styles.dueMedium,
	low: undefined,
};

type ActionIcon = NonNullable<AssistantFindingsItem['actions']>[number]['icon'];

function ActionGlyph({icon}: {icon: ActionIcon}) {
	switch (icon) {
		case 'task':
			return <CheckSquareIcon size={15} />;
		case 'reply':
			return <ArrowBendUpLeftIcon size={15} />;
		case 'assign':
			return <UserPlusIcon size={15} />;
		case 'done':
			return <CheckIcon size={15} />;
		case 'schedule':
			return <CalendarBlankIcon size={15} />;
		case 'log_time':
			return <TimerIcon size={15} />;
		case 'notes':
			return <NoteIcon size={15} />;
		default:
			return <ArrowSquareOutIcon size={15} />;
	}
}

/** "Sarah · today", "HOP-31 · In progress": short enough for a chip. */
export function sourceChipLabel(source: AssistantSourceItem): string {
	const who =
		source.kind === 'clickup'
			? source.title
			: (source.subtitle
					?.replace(/<[^>]*>/g, '')
					.trim()
					.split(/\s+/)[0] ??
					'') ||
				source.title;
	const label = source.meta ? `${who} · ${source.meta}` : who;
	return label.length > 34 ? `${label.slice(0, 33)}…` : label;
}

export function AssistantFindings({
	block,
	onAction,
	onSourceOpened,
	disabled = false,
}: {
	block: AssistantFindingsBlock;
	onAction?: (prompt: string, action?: string) => void;
	onSourceOpened?: (source: AssistantSourceItem) => void;
	disabled?: boolean;
}) {
	return (
		<section className={styles.findings} data-flx="konvo-assistant.findings">
			<header className={styles.head}>
				<h3 className={styles.title}>{block.title}</h3>
				{block.checked && <span className={styles.checked}>{block.checked}</span>}
			</header>
			{block.summary && <p className={styles.summary}>{block.summary}</p>}
			{block.groups.map((group, groupIndex) => (
				<div key={`${group.title ?? ''}:${groupIndex}`} className={styles.group}>
					{group.title && <h4 className={styles.groupTitle}>{group.title}</h4>}
					<ul className={styles.items}>
						{group.items.map((item, index) => (
							<li
								key={`${item.title}:${index}`}
								className={styles.item}
								data-urgency={item.urgency}
								data-flx="konvo-assistant.findings.item"
							>
								<span className={clsx(styles.ring, RING[item.urgency])} aria-hidden="true" />
								<div className={styles.body}>
									<strong className={styles.itemTitle}>{item.title}</strong>
									{(item.detail || item.due) && (
										<p className={styles.detail}>
											{item.detail}
											{item.detail && item.due ? ' ' : null}
											{item.due && <span className={clsx(styles.due, DUE[item.urgency])}>{item.due}</span>}
										</p>
									)}
									{item.sources && item.sources.length > 0 && (
										<div className={styles.sources}>
											{item.sources.map((source, sourceIndex) => (
												<Tooltip
													key={`${source.kind}:${source.message_id ?? source.task_id ?? sourceIndex}`}
													text={source.subtitle ? `${source.title} · ${source.subtitle}` : source.title}
												>
													<button
														type="button"
														className={styles.source}
														onClick={(event) => {
															onSourceOpened?.(source);
															openPreview(source, event.currentTarget);
														}}
														data-flx="konvo-assistant.findings.source"
													>
														<SourceMark kind={source.kind} size={12} />
														<span className={styles.sourceLabel}>{sourceChipLabel(source)}</span>
													</button>
												</Tooltip>
											))}
										</div>
									)}
								</div>
								{onAction && item.actions && item.actions.length > 0 && (
									<div className={styles.itemActions}>
										{item.actions.map((action) => (
											<Tooltip key={`${action.label}:${action.prompt}`} text={action.label}>
												<button
													type="button"
													className={styles.itemAction}
													aria-label={action.label}
													disabled={disabled}
													onClick={() => onAction(action.prompt, action.icon)}
													data-flx="konvo-assistant.findings.action"
												>
													<ActionGlyph icon={action.icon} />
												</button>
											</Tooltip>
										))}
									</div>
								)}
							</li>
						))}
					</ul>
				</div>
			))}
			{onAction && block.actions && block.actions.length > 0 && (
				<div className={styles.blockActions}>
					{block.actions.map((action) => (
						<button
							key={`${action.label}:${action.prompt}`}
							type="button"
							className={action.style === 'primary' ? styles.primary : styles.secondary}
							disabled={disabled}
							onClick={() => onAction(action.prompt, 'block')}
							data-flx="konvo-assistant.findings.block-action"
						>
							{action.label}
						</button>
					))}
				</div>
			)}
		</section>
	);
}
